/**
 * Thumbnail downscale filter.
 *
 * Capture renders at a multiple of the stored size and shrinks here, instead of
 * leaving the shrink to a canvas `drawImage` (a single bilinear step that skips
 * most source pixels at 4:1 and shimmers on thin lines and small text).
 *
 * - Lanczos-3 resample, separable, kernel stretched by the ratio so every source
 *   pixel counts. When the source is at least twice the output on both axes (and
 *   both sides are even), a 2x2 average runs first. Each result is clamped
 *   to the range of the source pixels it covers, so the negative lobes cannot ring
 *   (in linear light an undershoot next to a dark edge shows as a dark halo; an
 *   unsharp mask does the same, so there is none).
 * - Averaged in linear light: captured pixels are sRGB-encoded, and averaging
 *   the encoded values darkens every edge between light and dark.
 * - The input is premultiplied: the capture target is cleared to (0,0,0,0) and the
 *   multisample resolve blends colour with alpha, so an edge pixel holds colour x
 *   alpha (measured on real captures; see the PR). Each pixel is un-premultiplied to
 *   its straight sRGB colour, converted to linear, and weighted by alpha for the
 *   average, so transparent background does not pull a dark fringe into an edge.
 * - Ordered (Bayer 8x8) dither before the 8-bit quantisation, so smooth dark
 *   gradients do not step in 1-level bands.
 *
 * Pure functions on plain arrays; no DOM, so they are unit-testable.
 */

const SRGB_TO_LINEAR = new Float32Array(256);
for (let i = 0; i < 256; i++) {
    const c = i / 255;
    SRGB_TO_LINEAR[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/**
 * Premultiplied 8-bit channel `v` at alpha `a` (both 0..255) -> linear colour times alpha,
 * indexed `(a << 8) | v`. Un-premultiplies to the straight sRGB value first, so the
 * conversion to linear light acts on the colour and not on colour x alpha.
 */
const PREMUL_TO_LINEAR = new Float32Array(256 * 256);
for (let a = 1; a < 256; a++) {
    for (let v = 0; v < 256; v++) {
        const straight = Math.min(255, Math.round((v * 255) / a));
        PREMUL_TO_LINEAR[(a << 8) | v] = SRGB_TO_LINEAR[straight] * (a / 255);
    }
}

function linearToSrgb(l: number): number {
    if (l <= 0) return 0;
    if (l >= 1) return 1;
    return l <= 0.0031308 ? l * 12.92 : 1.055 * Math.pow(l, 1 / 2.4) - 0.055;
}

/** 8x8 Bayer matrix, thresholds in (0, 1) as (m + 0.5) / 64. */
const BAYER_8 = (() => {
    // Standard recursion: M(2n) = [[4M, 4M+2], [4M+3, 4M+1]].
    let m = [0];
    for (let n = 1; n < 8; n *= 2) {
        const size = n * 2;
        const next = new Array<number>(size * size);
        for (let y = 0; y < n; y++) {
            for (let x = 0; x < n; x++) {
                const v = 4 * m[y * n + x];
                next[y * size + x] = v;
                next[y * size + x + n] = v + 2;
                next[(y + n) * size + x] = v + 3;
                next[(y + n) * size + x + n] = v + 1;
            }
        }
        m = next;
    }
    return Float32Array.from(m, (v) => (v + 0.5) / 64);
})();

/** Dither threshold in (0, 1) for pixel (x, y). Exposed for tests. */
export function ditherThreshold(x: number, y: number): number {
    return BAYER_8[(y & 7) * 8 + (x & 7)];
}

const KERNEL_RADIUS = 3;

function sinc(x: number): number {
    if (x === 0) return 1;
    const px = Math.PI * x;
    return Math.sin(px) / px;
}

/** Lanczos-3 kernel value at `x` (in output pixels). */
function kernelValue(x: number): number {
    const ax = Math.abs(x);
    return ax < 3 ? sinc(ax) * sinc(ax / 3) : 0;
}

/**
 * Per-axis weights. Output pixel `o` is centred at source `(o + 0.5) * ratio`; the
 * kernel is stretched by `ratio` so every source pixel contributes (no aliasing),
 * and the taps are normalised to sum to 1.
 */
function axisWeights(srcLen: number, outLen: number): { start: Int32Array; count: Int32Array; w: Float32Array; stride: number } {
    const ratio = srcLen / outLen;
    const radius = KERNEL_RADIUS * Math.max(1, ratio);
    const stride = Math.ceil(radius * 2) + 2;
    const start = new Int32Array(outLen);
    const count = new Int32Array(outLen);
    const w = new Float32Array(outLen * stride);
    for (let o = 0; o < outLen; o++) {
        const c = (o + 0.5) * ratio;
        const s = Math.max(0, Math.floor(c - radius));
        const e = Math.min(srcLen - 1, Math.ceil(c + radius) - 1);
        start[o] = s;
        count[o] = e - s + 1;
        let sum = 0;
        for (let x = s; x <= e; x++) {
            const v = kernelValue((x + 0.5 - c) / Math.max(1, ratio));
            w[o * stride + (x - s)] = v;
            sum += v;
        }
        for (let i = 0; i < count[o]; i++) w[o * stride + i] /= sum;
    }
    return { start, count, w, stride };
}

/**
 * Shrink premultiplied RGBA8 sRGB pixels, top-down (already flipped), to `outW` x `outH`.
 * Output is quantised to 8 bits with an ordered dither.
 * `src` must be `srcW * srcH * 4` bytes; output dimensions must not exceed the source.
 */
export function downscaleRgba(
    src: Uint8ClampedArray | Uint8Array,
    srcW: number,
    srcH: number,
    outW: number,
    outH: number,
): Uint8ClampedArray {
    if (srcW === outW && srcH === outH) {
        // Nothing to resample, but the readback is premultiplied and the PNG takes straight alpha.
        const same = new Uint8ClampedArray(src.length);
        for (let i = 0; i < src.length; i += 4) {
            const a = src[i + 3];
            same[i + 3] = a;
            if (a === 0) continue;
            const k = 255 / a;
            same[i] = src[i] * k; same[i + 1] = src[i + 1] * k; same[i + 2] = src[i + 2] * k;
        }
        return same;
    }
    const out = new Uint8ClampedArray(outW * outH * 4);

    // Premultiplied linear copy of the source (colour in linear light times alpha), so the taps below are plain float reads.
    // A wide kernel over a 4:1 shrink is mostly wasted work, so when the source is at
    // least 2x the output a 2x2 average is taken on the way in and the kernel runs over
    // the half-size image. That is cheaper than the full-size kernel and not identical to
    // it; the clamp below then sees the half-size neighbourhood.
    const halve = srcW >= outW * 2 && srcH >= outH * 2 && srcW % 2 === 0 && srcH % 2 === 0;
    let lp: Float32Array;
    if (halve) {
        const hw = srcW / 2, hh = srcH / 2;
        lp = new Float32Array(hw * hh * 4);
        for (let y = 0; y < hh; y++) {
            for (let x = 0; x < hw; x++) {
                const i0 = (2 * y * srcW + 2 * x) * 4, i1 = i0 + srcW * 4, o = (y * hw + x) * 4;
                const a0 = src[i0 + 3] << 8, a1 = src[i0 + 7] << 8, a2 = src[i1 + 3] << 8, a3 = src[i1 + 7] << 8;
                const r = PREMUL_TO_LINEAR[a0 | src[i0]] + PREMUL_TO_LINEAR[a1 | src[i0 + 4]] + PREMUL_TO_LINEAR[a2 | src[i1]] + PREMUL_TO_LINEAR[a3 | src[i1 + 4]];
                const g = PREMUL_TO_LINEAR[a0 | src[i0 + 1]] + PREMUL_TO_LINEAR[a1 | src[i0 + 5]] + PREMUL_TO_LINEAR[a2 | src[i1 + 1]] + PREMUL_TO_LINEAR[a3 | src[i1 + 5]];
                const b = PREMUL_TO_LINEAR[a0 | src[i0 + 2]] + PREMUL_TO_LINEAR[a1 | src[i0 + 6]] + PREMUL_TO_LINEAR[a2 | src[i1 + 2]] + PREMUL_TO_LINEAR[a3 | src[i1 + 6]];
                const k = 0.25;
                lp[o] = r * k; lp[o + 1] = g * k; lp[o + 2] = b * k; lp[o + 3] = ((a0 + a1 + a2 + a3) >> 8) * (0.25 / 255);
            }
        }
        srcW = hw; srcH = hh;
    } else {
        lp = new Float32Array(srcW * srcH * 4);
        for (let i = 0, n = srcW * srcH * 4; i < n; i += 4) {
            const a8 = src[i + 3], ah = a8 << 8;
            lp[i] = PREMUL_TO_LINEAR[ah | src[i]];
            lp[i + 1] = PREMUL_TO_LINEAR[ah | src[i + 1]];
            lp[i + 2] = PREMUL_TO_LINEAR[ah | src[i + 2]];
            lp[i + 3] = a8 / 255;
        }
    }
    const wx = axisWeights(srcW, outW);
    const wy = axisWeights(srcH, outH);

    // Pass 1: horizontal (outW x srcH x 4).
    const tmp = new Float32Array(outW * srcH * 4);
    for (let y = 0; y < srcH; y++) {
        const row = y * srcW * 4;
        for (let ox = 0; ox < outW; ox++) {
            let r = 0, g = 0, b = 0, a = 0;
            const s = wx.start[ox], n = wx.count[ox], wb = ox * wx.stride;
            let i = row + s * 4;
            for (let k = 0; k < n; k++, i += 4) {
                const w = wx.w[wb + k];
                r += lp[i] * w; g += lp[i + 1] * w; b += lp[i + 2] * w; a += lp[i + 3] * w;
            }
            const t = (y * outW + ox) * 4;
            tmp[t] = r; tmp[t + 1] = g; tmp[t + 2] = b; tmp[t + 3] = a;
        }
    }

    // Pass 2: vertical, into premultiplied linear (outW x outH x 4).
    const lin = new Float32Array(outW * outH * 4);
    const ratioX = srcW / outW, ratioY = srcH / outH;
    for (let oy = 0; oy < outH; oy++) {
        const s = wy.start[oy], n = wy.count[oy], wb = oy * wy.stride;
        const y0 = Math.max(0, Math.floor(oy * ratioY) - 1), y1 = Math.min(srcH - 1, Math.ceil((oy + 1) * ratioY));
        for (let ox = 0; ox < outW; ox++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let k = 0; k < n; k++) {
                const t = ((s + k) * outW + ox) * 4;
                const w = wy.w[wb + k];
                r += tmp[t] * w; g += tmp[t + 1] * w; b += tmp[t + 2] * w; a += tmp[t + 3] * w;
            }
            const o = (oy * outW + ox) * 4;
            // Anti-ringing: the negative lobes of the kernel may not push a result past the
            // range of the source pixels it covers (this footprint plus one pixel). In linear
            // light an undershoot next to a dark edge would otherwise show as a dark halo.
            const x0 = Math.max(0, Math.floor(ox * ratioX) - 1), x1 = Math.min(srcW - 1, Math.ceil((ox + 1) * ratioX));
            let lr = Infinity, lg = Infinity, lb = Infinity, la = Infinity;
            let hr = -Infinity, hg = -Infinity, hb = -Infinity, ha = -Infinity;
            for (let y = y0; y <= y1; y++) {
                let i = (y * srcW + x0) * 4;
                for (let x = x0; x <= x1; x++, i += 4) {
                    const pr = lp[i], pg = lp[i + 1], pb = lp[i + 2], pa = lp[i + 3];
                    if (pr < lr) lr = pr; if (pr > hr) hr = pr;
                    if (pg < lg) lg = pg; if (pg > hg) hg = pg;
                    if (pb < lb) lb = pb; if (pb > hb) hb = pb;
                    if (pa < la) la = pa; if (pa > ha) ha = pa;
                }
            }
            r = r < lr ? lr : r > hr ? hr : r;
            g = g < lg ? lg : g > hg ? hg : g;
            b = b < lb ? lb : b > hb ? hb : b;
            a = a < la ? la : a > ha ? ha : a;
            lin[o] = r; lin[o + 1] = g; lin[o + 2] = b; lin[o + 3] = a;
        }
    }

    // Un-premultiply, encode, dither, quantise. Negative kernel lobes can leave the
    // result slightly outside [0, alpha]; clamp there.
    for (let oy = 0; oy < outH; oy++) {
        for (let ox = 0; ox < outW; ox++) {
            const o = (oy * outW + ox) * 4;
            const d = ditherThreshold(ox, oy);
            const a = Math.min(1, Math.max(0, lin[o + 3]));
            if (a > 1e-6) {
                const ia = 1 / a;
                out[o] = Math.floor(linearToSrgb(lin[o] * ia) * 255 + d);
                out[o + 1] = Math.floor(linearToSrgb(lin[o + 1] * ia) * 255 + d);
                out[o + 2] = Math.floor(linearToSrgb(lin[o + 2] * ia) * 255 + d);
            }
            out[o + 3] = Math.floor(a * 255 + d);
        }
    }
    return out;
}

/** Flip bottom-up RTT rows to top-down. */
export function flipRows(src: Uint8ClampedArray | Uint8Array, width: number, height: number): Uint8ClampedArray {
    const out = new Uint8ClampedArray(src.length);
    const rowBytes = width * 4;
    for (let y = 0; y < height; y++) {
        out.set(src.subarray((height - 1 - y) * rowBytes, (height - y) * rowBytes), y * rowBytes);
    }
    return out;
}
